// src/components/system/providers/ConfigProvider.js
// Configuration Provider Component
// Created: 2025-01-20 16:54:00 UTC+8
// Purpose: React context provider for website configuration management

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { ConfigManager } from '../utils/configManager';
import { SEOManager } from '../seo/SEOManager';
import { I18nManager } from '../i18n/i18nManager';

/**
 * Configuration Context
 */
const ConfigContext = createContext({
  config: null,
  loading: true,
  error: null,
  updateConfig: () => {},
  resetConfig: () => {},
  exportConfig: () => {},
  importConfig: () => {},
  createCustomerConfig: () => {},
  seoManager: null,
  i18nManager: null,
  configManager: null
});

/**
 * Configuration Provider Component
 * Provides configuration context to the entire application
 */
export const ConfigProvider = ({ children, initialConfig = null }) => {
  const [config, setConfig] = useState(initialConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configManager] = useState(() => new ConfigManager());
  const [seoManager, setSeoManager] = useState(null);
  const [i18nManager, setI18nManager] = useState(null);

  /**
   * Initialize configuration and managers
   */
  const initializeConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load configuration
      const loadedConfig = await configManager.loadConfig();
      setConfig(loadedConfig);

      // Initialize SEO Manager
      const seoMgr = new SEOManager(loadedConfig);
      setSeoManager(seoMgr);

      // Initialize I18n Manager
      const i18nMgr = new I18nManager(loadedConfig);
      await i18nMgr.initialize();
      setI18nManager(i18nMgr);

      // SEO manager is ready for use

      console.log('Configuration system initialized successfully');
    } catch (err) {
      console.error('Failed to initialize configuration:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [configManager]);

  /**
   * Update configuration
   */
  const updateConfig = useCallback(async (newConfig, persist = true) => {
    try {
      setError(null);
      
      // Validate configuration
      const validationResult = configManager.validateConfig(newConfig);
      if (!validationResult.isValid) {
        throw new Error(`Configuration validation failed: ${validationResult.errors.join(', ')}`);
      }

      // Update state
      setConfig(newConfig);

      // Persist to storage if requested
      if (persist) {
        await configManager.saveConfig(newConfig);
      }

      // Update SEO settings
      if (seoManager) {
        seoManager.updateConfig(newConfig);
      }

      // Update i18n settings
      if (i18nManager) {
        i18nManager.config = newConfig;
        await i18nManager.loadWebsiteTranslations();
      }

      // Dispatch custom event for other components
      window.dispatchEvent(new CustomEvent('configUpdated', {
        detail: { config: newConfig }
      }));

      console.log('Configuration updated successfully');
      return true;
    } catch (err) {
      console.error('Failed to update configuration:', err);
      setError(err.message);
      return false;
    }
  }, [configManager, seoManager, i18nManager]);

  /**
   * Update specific configuration section
   */
  const updateConfigSection = useCallback(async (section, data) => {
    if (!config) return false;

    const newConfig = {
      ...config,
      [section]: {
        ...config[section],
        ...data
      }
    };

    return await updateConfig(newConfig);
  }, [config, updateConfig]);

  /**
   * Reset configuration to default
   */
  const resetConfig = useCallback(async () => {
    try {
      setError(null);
      const defaultConfig = configManager.getDefaultConfig();
      await updateConfig(defaultConfig);
      console.log('Configuration reset to default');
      return true;
    } catch (err) {
      console.error('Failed to reset configuration:', err);
      setError(err.message);
      return false;
    }
  }, [configManager, updateConfig]);

  /**
   * Export configuration
   */
  const exportConfig = useCallback(() => {
    try {
      if (!config) {
        throw new Error('No configuration to export');
      }
      configManager.exportConfig(config);
      return true;
    } catch (err) {
      console.error('Failed to export configuration:', err);
      setError(err.message);
      return false;
    }
  }, [config, configManager]);

  /**
   * Import configuration
   */
  const importConfig = useCallback(async (configData) => {
    try {
      setError(null);
      const importedConfig = configManager.importConfig(configData);
      await updateConfig(importedConfig);
      console.log('Configuration imported successfully');
      return true;
    } catch (err) {
      console.error('Failed to import configuration:', err);
      setError(err.message);
      return false;
    }
  }, [configManager, updateConfig]);

  /**
   * Create customer configuration
   */
  const createCustomerConfig = useCallback(async (customerName, baseConfig = null) => {
    try {
      setError(null);
      const customerConfig = configManager.createCustomerConfig(
        customerName,
        baseConfig || config
      );
      await updateConfig(customerConfig);
      console.log(`Customer configuration created for ${customerName}`);
      return customerConfig;
    } catch (err) {
      console.error('Failed to create customer configuration:', err);
      setError(err.message);
      return null;
    }
  }, [config, configManager, updateConfig]);

  /**
   * Get configuration value by path
   */
  const getConfigValue = useCallback((path, defaultValue = null) => {
    if (!config) return defaultValue;
    
    const keys = path.split('.');
    let value = config;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return defaultValue;
      }
    }
    
    return value;
  }, [config]);

  /**
   * Set configuration value by path
   */
  const setConfigValue = useCallback(async (path, value) => {
    if (!config) return false;

    const keys = path.split('.');
    const newConfig = JSON.parse(JSON.stringify(config)); // Deep clone
    let current = newConfig;

    // Navigate to the parent of the target key
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!current[key] || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    // Set the value
    current[keys[keys.length - 1]] = value;

    return await updateConfig(newConfig);
  }, [config, updateConfig]);

  /**
   * Check if feature is enabled
   */
  const isFeatureEnabled = useCallback((featureName) => {
    return getConfigValue(`features.${featureName}`, false);
  }, [getConfigValue]);

  /**
   * Get theme configuration
   */
  const getThemeConfig = useCallback(() => {
    return getConfigValue('theme', {});
  }, [getConfigValue]);

  /**
   * Get SEO configuration
   */
  const getSEOConfig = useCallback(() => {
    return getConfigValue('seo', {});
  }, [getConfigValue]);

  /**
   * Get i18n configuration
   */
  const getI18nConfig = useCallback(() => {
    return getConfigValue('i18n', {});
  }, [getConfigValue]);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    initializeConfig();
  }, [initializeConfig]);

  /**
   * Listen for storage changes (for multi-tab synchronization)
   */
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'websiteConfig' && event.newValue) {
        try {
          const newConfig = JSON.parse(event.newValue);
          setConfig(newConfig);
          console.log('Configuration synchronized from storage');
        } catch (err) {
          console.error('Failed to sync configuration from storage:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /**
   * Apply theme changes to document
   */
  useEffect(() => {
    if (config?.theme) {
      const { primaryColor, secondaryColor, darkMode } = config.theme;
      
      // Apply CSS custom properties
      const root = document.documentElement;
      if (primaryColor) {
        root.style.setProperty('--primary-color', primaryColor);
      }
      if (secondaryColor) {
        root.style.setProperty('--secondary-color', secondaryColor);
      }
      
      // Apply dark mode
      if (darkMode === 'dark') {
        root.classList.add('dark-mode');
      } else if (darkMode === 'light') {
        root.classList.remove('dark-mode');
      } else {
        // Auto mode - respect system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.classList.toggle('dark-mode', prefersDark);
      }
    }
  }, [config?.theme]);

  /**
   * Context value
   */
  const contextValue = {
    // State
    config,
    loading,
    error,
    
    // Managers
    configManager,
    seoManager,
    i18nManager,
    
    // Configuration methods
    updateConfig,
    updateConfigSection,
    resetConfig,
    exportConfig,
    importConfig,
    createCustomerConfig,
    
    // Utility methods
    getConfigValue,
    setConfigValue,
    isFeatureEnabled,
    getThemeConfig,
    getSEOConfig,
    getI18nConfig,
    
    // Re-initialization
    initializeConfig
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};

/**
 * Hook to use configuration context
 */
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

/**
 * Hook to use specific configuration section
 */
export const useConfigSection = (section) => {
  const { getConfigValue, setConfigValue } = useConfig();
  
  const sectionData = getConfigValue(section, {});
  
  const updateSection = useCallback(async (data) => {
    const updates = {};
    Object.keys(data).forEach(key => {
      updates[`${section}.${key}`] = data[key];
    });
    
    const promises = Object.entries(updates).map(([path, value]) => 
      setConfigValue(path, value)
    );
    
    const results = await Promise.all(promises);
    return results.every(result => result === true);
  }, [section, setConfigValue]);
  
  return [sectionData, updateSection];
};

/**
 * Hook to use theme configuration
 */
export const useTheme = () => {
  const { getThemeConfig, setConfigValue } = useConfig();
  
  const theme = getThemeConfig();
  
  const updateTheme = useCallback(async (themeData) => {
    const promises = Object.entries(themeData).map(([key, value]) => 
      setConfigValue(`theme.${key}`, value)
    );
    
    const results = await Promise.all(promises);
    return results.every(result => result === true);
  }, [setConfigValue]);
  
  return [theme, updateTheme];
};

/**
 * Hook to use SEO configuration
 */
export const useSEO = () => {
  const { getSEOConfig, seoManager, setConfigValue } = useConfig();
  
  const seo = getSEOConfig();
  
  const updateSEO = useCallback(async (seoData) => {
    const promises = Object.entries(seoData).map(([key, value]) => 
      setConfigValue(`seo.${key}`, value)
    );
    
    const results = await Promise.all(promises);
    const success = results.every(result => result === true);
    
    // SEO manager will automatically use updated config
    
    return success;
  }, [setConfigValue, seoManager]);
  
  return [seo, updateSEO, seoManager];
};

/**
 * Hook to use i18n configuration
 */
export const useI18n = () => {
  const { getI18nConfig, i18nManager, setConfigValue } = useConfig();
  
  const i18n = getI18nConfig();
  
  const updateI18n = useCallback(async (i18nData) => {
    const promises = Object.entries(i18nData).map(([key, value]) => 
      setConfigValue(`i18n.${key}`, value)
    );
    
    const results = await Promise.all(promises);
    const success = results.every(result => result === true);
    
    // Reload i18n translations
    if (success && i18nManager) {
      await i18nManager.loadWebsiteTranslations();
    }
    
    return success;
  }, [setConfigValue, i18nManager]);
  
  const changeLanguage = useCallback(async (language) => {
    if (i18nManager) {
      return await i18nManager.changeLanguage(language);
    }
    return false;
  }, [i18nManager]);
  
  const getCurrentLanguage = useCallback(() => {
    return i18nManager?.getCurrentLanguage() || 'en';
  }, [i18nManager]);
  
  return [i18n, updateI18n, i18nManager, { changeLanguage, getCurrentLanguage }];
};

/**
 * Hook to use feature flags
 */
export const useFeatures = () => {
  const { getConfigValue, setConfigValue } = useConfig();
  
  const features = getConfigValue('features', {});
  
  const isEnabled = useCallback((featureName) => {
    return getConfigValue(`features.${featureName}`, false);
  }, [getConfigValue]);
  
  const toggleFeature = useCallback(async (featureName) => {
    const currentValue = isEnabled(featureName);
    return await setConfigValue(`features.${featureName}`, !currentValue);
  }, [isEnabled, setConfigValue]);
  
  const enableFeature = useCallback(async (featureName) => {
    return await setConfigValue(`features.${featureName}`, true);
  }, [setConfigValue]);
  
  const disableFeature = useCallback(async (featureName) => {
    return await setConfigValue(`features.${featureName}`, false);
  }, [setConfigValue]);
  
  return {
    features,
    isEnabled,
    toggleFeature,
    enableFeature,
    disableFeature
  };
};

export default ConfigProvider;